/**
 * FikoMini3D - Dialog içinde kullanılabilecek bağımsız mini 3D viewer
 *
 * TASARIM PRENSİBİ:
 * - Circular import'lardan kaçınmak için FikoThreeJsComponent'ten TAMAMEN BAĞIMSIZ
 * - Sadece Three.js kullanır, diğer Fiko* sınıflarına bağımlı değil
 * - Lightweight ve hızlı başlatma
 * - Dialog içinde gömülü kullanım için optimize edilmiş
 *
 * KULLANIM:
 * ```html
 * <fikoyt-mini-3d
 *   [genislik]="300"
 *   [yukseklik]="250"
 *   (hazir)="mini3dHazir($event)">
 * </fikoyt-mini-3d>
 * ```
 */

import {
  Component,
  ElementRef,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  NgZone
} from '@angular/core';
import { CommonModule } from '@angular/common';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export interface FikoMini3DEvent {
  sahne: THREE.Scene;
  kamera: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  renderer: THREE.WebGLRenderer;
  kontrol: OrbitControls;
  mini3d: FikoMini3DComponent;
}

@Component({
  selector: 'fikoyt-mini-3d',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fiko-mini-3d-container"
         [style.width.px]="genislik"
         [style.height.px]="yukseklik"
         [style.background]="arkaplanRenk">
      <canvas #miniCanvas></canvas>
      <div class="mini-3d-overlay" *ngIf="yukleniyor">
        <div class="mini-loader"></div>
      </div>
    </div>
  `,
  styles: [`
    .fiko-mini-3d-container {
      position: relative;
      overflow: hidden;
      border-radius: 4px;
      border: 1px solid rgba(0, 255, 255, 0.3);
    }

    .fiko-mini-3d-container canvas {
      width: 100% !important;
      height: 100% !important;
      display: block;
    }

    .mini-3d-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .mini-loader {
      width: 30px;
      height: 30px;
      border: 3px solid rgba(0, 255, 255, 0.3);
      border-top-color: #00ffff;
      border-radius: 50%;
      animation: mini-spin 1s linear infinite;
    }

    @keyframes mini-spin {
      to { transform: rotate(360deg); }
    }
  `]
})
export class FikoMini3DComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('miniCanvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  // Boyut ayarları
  @Input() genislik: number = 300;
  @Input() yukseklik: number = 250;

  // Görünüm ayarları
  @Input() arkaplanRenk: string = '#1a1a2e';
  @Input() arkaplanSahne: number = 0x1a1a2e;
  @Input() gridGoster: boolean = true;
  @Input() axisGoster: boolean = true;

  // Kamera ayarları
  @Input() ortografik: boolean = false;
  @Input() kameraPozisyon: { x: number; y: number; z: number } = { x: 5, y: 5, z: 5 };
  @Input() kameraHedef: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };

  // Kontrol ayarları
  @Input() kontrolAktif: boolean = true;
  @Input() otoDon: boolean = false;
  @Input() otoDonHiz: number = 0.005;

  // Events
  @Output() hazir = new EventEmitter<FikoMini3DEvent>();
  @Output() renderOnce = new EventEmitter<void>();
  @Output() tiklandi = new EventEmitter<THREE.Intersection[]>();
  @Output() ciftTiklandi = new EventEmitter<THREE.Intersection[]>();   // çift-tık: nesneye → düzenle; boşluğa → ekrana sığdır

  // Three.js nesneleri
  sahne!: THREE.Scene;
  kamera!: THREE.PerspectiveCamera | THREE.OrthographicCamera;
  renderer!: THREE.WebGLRenderer;
  kontrol!: OrbitControls;

  // İç durum
  yukleniyor = false;
  private animasyonId: number = 0;
  private disposed = false;
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();

  // Işıklar
  private ambientLight!: THREE.AmbientLight;
  private directionalLight!: THREE.DirectionalLight;

  // Yardımcılar
  private gridHelper?: THREE.GridHelper;
  private axisHelper?: THREE.AxesHelper;

  constructor(
    private ngZone: NgZone,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Initialization yapılacak
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.baslat();
    });
  }

  ngOnDestroy(): void {
    this.temizle();
  }

  /**
   * 3D sahneyi başlat
   */
  private baslat(): void {
    const canvas = this.canvasRef.nativeElement;

    // Renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(this.genislik, this.yukseklik);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(this.arkaplanSahne, 1);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Sahne
    this.sahne = new THREE.Scene();
    this.sahne.background = new THREE.Color(this.arkaplanSahne);

    // Kamera
    if (this.ortografik) {
      const aspect = this.genislik / this.yukseklik;
      const frustumSize = 10;
      this.kamera = new THREE.OrthographicCamera(
        -frustumSize * aspect / 2,
        frustumSize * aspect / 2,
        frustumSize / 2,
        -frustumSize / 2,
        0.1,
        1000
      );
    } else {
      this.kamera = new THREE.PerspectiveCamera(
        45,
        this.genislik / this.yukseklik,
        0.1,
        1000
      );
    }

    this.kamera.position.set(
      this.kameraPozisyon.x,
      this.kameraPozisyon.y,
      this.kameraPozisyon.z
    );
    this.kamera.lookAt(
      this.kameraHedef.x,
      this.kameraHedef.y,
      this.kameraHedef.z
    );

    // Kontroller
    this.kontrol = new OrbitControls(this.kamera, this.renderer.domElement);
    this.kontrol.enableDamping = true;
    this.kontrol.dampingFactor = 0.05;
    this.kontrol.enabled = this.kontrolAktif;
    this.kontrol.target.set(
      this.kameraHedef.x,
      this.kameraHedef.y,
      this.kameraHedef.z
    );
    this.kontrol.update();

    // Işıklar
    this.isikEkle();

    // Grid ve Axis
    if (this.gridGoster) {
      this.gridHelper = new THREE.GridHelper(10, 10, 0x444444, 0x222222);
      this.sahne.add(this.gridHelper);
    }

    if (this.axisGoster) {
      this.axisHelper = new THREE.AxesHelper(2);
      this.sahne.add(this.axisHelper);
    }

    // Tıklama event'i
    canvas.addEventListener('click', this.tiklamaHandler.bind(this));
    canvas.addEventListener('dblclick', this.ciftTiklamaHandler.bind(this));

    // Animasyon döngüsü
    this.animasyonBaslat();

    // Hazır event'i gönder
    this.ngZone.run(() => {
      this.hazir.emit({
        sahne: this.sahne,
        kamera: this.kamera,
        renderer: this.renderer,
        kontrol: this.kontrol,
        mini3d: this
      });
      this.cd.detectChanges();
    });
  }

  /**
   * Işıkları ekle
   */
  private isikEkle(): void {
    // Ambient ışık
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    this.sahne.add(this.ambientLight);

    // Directional ışık
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
    this.directionalLight.position.set(5, 10, 7);
    this.directionalLight.castShadow = true;
    this.directionalLight.shadow.mapSize.width = 512;
    this.directionalLight.shadow.mapSize.height = 512;
    this.sahne.add(this.directionalLight);

    // Arka ışık (rim light efekti)
    const backLight = new THREE.DirectionalLight(0x4488ff, 0.3);
    backLight.position.set(-5, 5, -5);
    this.sahne.add(backLight);
  }

  /**
   * Animasyon döngüsünü başlat
   */
  private animasyonBaslat(): void {
    if (this.disposed) return;

    const animate = () => {
      if (this.disposed) return;

      this.animasyonId = requestAnimationFrame(animate);

      // Kontrol güncelle
      if (this.kontrol) {
        this.kontrol.update();
      }

      // Oto dönüş
      if (this.otoDon && this.sahne.children.length > 0) {
        this.sahne.rotation.y += this.otoDonHiz;
      }

      // Render
      if (this.renderer && this.sahne && this.kamera) {
        this.renderer.render(this.sahne, this.kamera);
      }
    };

    animate();
  }

  /**
   * Tıklama handler
   */
  private tiklamaHandler(event: MouseEvent): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();

    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.kamera);

    const intersects = this.raycaster.intersectObjects(this.sahne.children, true);

    if (intersects.length > 0) {
      this.ngZone.run(() => {
        this.tiklandi.emit(intersects);
      });
    }
  }

  /**
   * Çift tıklama handler - nesneye tıklandıysa düzenleme event'i; boşluğa tıklandıysa ekrana sığdır.
   */
  private ciftTiklamaHandler(event: MouseEvent): void {
    const canvas = this.canvasRef.nativeElement;
    const rect = canvas.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.kamera);
    const intersects = this.raycaster.intersectObjects(this.sahne.children, true);
    if (intersects.length > 0) { this.ngZone.run(() => this.ciftTiklandi.emit(intersects)); }
    else this.ekranaSigdir();
  }

  // ==================== PUBLIC API ====================

  /**
   * Mesh ekle
   */
  meshEkle(geometry: THREE.BufferGeometry, material?: THREE.Material): THREE.Mesh {
    const mat = material || new THREE.MeshStandardMaterial({
      color: 0x00aaff,
      metalness: 0.3,
      roughness: 0.7
    });

    const mesh = new THREE.Mesh(geometry, mat);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    this.sahne.add(mesh);

    return mesh;
  }

  /**
   * Object3D ekle
   */
  objeEkle(obj: THREE.Object3D): void {
    this.sahne.add(obj);
  }

  /**
   * Object3D kaldır
   */
  objeKaldir(obj: THREE.Object3D): void {
    this.sahne.remove(obj);
  }

  /**
   * Sahneyi temizle (ışıklar ve yardımcılar hariç)
   */
  sahneyiTemizle(): void {
    const kalacaklar = [
      this.ambientLight,
      this.directionalLight,
      this.gridHelper,
      this.axisHelper
    ].filter(Boolean);

    const silinecekler = this.sahne.children.filter(
      child => !kalacaklar.includes(child as any)
    );

    silinecekler.forEach(child => {
      this.sahne.remove(child);
      if ((child as THREE.Mesh).geometry) {
        (child as THREE.Mesh).geometry.dispose();
      }
      if ((child as THREE.Mesh).material) {
        const mat = (child as THREE.Mesh).material;
        if (Array.isArray(mat)) {
          mat.forEach(m => m.dispose());
        } else {
          mat.dispose();
        }
      }
    });
  }

  /**
   * Modeli ekrana sığdır
   */
  ekranaSigdir(padding: number = 1.2): void {
    const box = new THREE.Box3().setFromObject(this.sahne);

    if (box.isEmpty()) return;

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Kamera mesafesi
    const fov = this.kamera instanceof THREE.PerspectiveCamera
      ? this.kamera.fov * (Math.PI / 180)
      : 1;
    const distance = maxDim / (2 * Math.tan(fov / 2)) * padding;

    // Kamerayı konumlandır
    const direction = this.kamera.position.clone().sub(this.kontrol.target).normalize();
    this.kamera.position.copy(center).add(direction.multiplyScalar(distance));
    this.kontrol.target.copy(center);
    this.kontrol.update();
  }

  /**
   * Kamera pozisyonunu ayarla
   */
  kameraPozisyonAyarla(x: number, y: number, z: number, animate: boolean = true): void {
    if (animate) {
      // Basit lerp animasyonu
      const startPos = this.kamera.position.clone();
      const endPos = new THREE.Vector3(x, y, z);
      let t = 0;

      const animateCamera = () => {
        t += 0.05;
        if (t >= 1) {
          this.kamera.position.copy(endPos);
          return;
        }

        this.kamera.position.lerpVectors(startPos, endPos, t);
        requestAnimationFrame(animateCamera);
      };

      animateCamera();
    } else {
      this.kamera.position.set(x, y, z);
    }

    this.kontrol.update();
  }

  /**
   * Boyutu güncelle
   */
  boyutGuncelle(genislik: number, yukseklik: number): void {
    this.genislik = genislik;
    this.yukseklik = yukseklik;

    if (this.kamera instanceof THREE.PerspectiveCamera) {
      this.kamera.aspect = genislik / yukseklik;
      this.kamera.updateProjectionMatrix();
    } else if (this.kamera instanceof THREE.OrthographicCamera) {
      const aspect = genislik / yukseklik;
      const frustumSize = 10;
      this.kamera.left = -frustumSize * aspect / 2;
      this.kamera.right = frustumSize * aspect / 2;
      this.kamera.top = frustumSize / 2;
      this.kamera.bottom = -frustumSize / 2;
      this.kamera.updateProjectionMatrix();
    }

    this.renderer.setSize(genislik, yukseklik);
  }

  /**
   * Tek frame render et
   */
  ciz(): void {
    if (this.renderer && this.sahne && this.kamera) {
      this.renderer.render(this.sahne, this.kamera);
      this.renderOnce.emit();
    }
  }

  /**
   * Grid görünürlüğünü değiştir
   */
  gridToggle(goster: boolean): void {
    if (this.gridHelper) {
      this.gridHelper.visible = goster;
    }
  }

  /**
   * Axis görünürlüğünü değiştir
   */
  axisToggle(goster: boolean): void {
    if (this.axisHelper) {
      this.axisHelper.visible = goster;
    }
  }

  /**
   * Oto dönüşü başlat/durdur
   */
  otoDonToggle(aktif: boolean): void {
    this.otoDon = aktif;
  }

  /**
   * Screenshot al
   */
  screenshot(): string {
    this.ciz();
    return this.renderer.domElement.toDataURL('image/png');
  }

  /**
   * Temizlik
   */
  private temizle(): void {
    this.disposed = true;

    if (this.animasyonId) {
      cancelAnimationFrame(this.animasyonId);
    }

    // Event listener temizliği
    const canvas = this.canvasRef?.nativeElement;
    if (canvas) {
      canvas.removeEventListener('click', this.tiklamaHandler.bind(this));
      canvas.removeEventListener('dblclick', this.ciftTiklamaHandler.bind(this));
    }

    // Kontrol temizliği
    if (this.kontrol) {
      this.kontrol.dispose();
    }

    // Sahne temizliği
    if (this.sahne) {
      this.sahne.traverse((child) => {
        if ((child as THREE.Mesh).geometry) {
          (child as THREE.Mesh).geometry.dispose();
        }
        if ((child as THREE.Mesh).material) {
          const mat = (child as THREE.Mesh).material;
          if (Array.isArray(mat)) {
            mat.forEach(m => m.dispose());
          } else {
            mat.dispose();
          }
        }
      });
      this.sahne.clear();
    }

    // Renderer temizliği
    if (this.renderer) {
      this.renderer.dispose();
      this.renderer.forceContextLoss();
    }
  }
}
